import React, { useEffect } from 'react';
import { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { FormField } from '../../../types/form';
import { AlertCircle } from 'lucide-react';
import { SignatureField } from './SignatureField';
import { evaluateCalculation } from '../../../features/forms/utils/calculations';

interface DynamicFieldProps {
  field: FormField;
  register: UseFormRegister<any>;
  errors: FieldErrors;
  value?: any;
  theme?: 'light' | 'dark';
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  allFields?: FormField[];
}

export const DynamicField: React.FC<DynamicFieldProps> = ({
  field,
  register,
  errors,
  value,
  theme = 'light',
  watch,
  setValue,
  allFields = [],
}) => {
  const error = errors[field.name];
  const isDark = theme === 'dark';

  // Watch all form values for calculations
  const formValues = watch();

  // Handle calculated fields
  useEffect(() => {
    if (field.type === 'calculated' && field.calculation) {
      const result = evaluateCalculation(field.calculation, formValues);
      if (result !== null) {
        setValue(field.name, result);
      }
    }
  }, [formValues, field.type, field.calculation, field.name, setValue]);

  // Grid span classes for consistent row alignment
  const widthClass =
    field.width === 'half'
      ? 'col-span-12 md:col-span-6'
      : field.width === 'third'
      ? 'col-span-12 md:col-span-4'
      : 'col-span-12';

  const baseInputClass = `w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  } ${error ? 'border-red-500 focus:ring-red-500' : ''}`;

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`;

  const renderField = () => {
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
              minLength: field.validation?.minLength ? {
                value: field.validation.minLength,
                message: `Minimum ${field.validation.minLength} characters required`
              } : undefined,
              maxLength: field.validation?.maxLength ? {
                value: field.validation.maxLength,
                message: `Maximum ${field.validation.maxLength} characters allowed`
              } : undefined,
              pattern: field.validation?.pattern ? {
                value: new RegExp(field.validation.pattern),
                message: field.validation.patternMessage || 'Invalid format'
              } : undefined,
            })}
            placeholder={field.placeholder}
            defaultValue={field.defaultValue}
            rows={4}
            className={baseInputClass}
            aria-label={field.label}
            aria-required={field.required}
            aria-invalid={!!error}
            aria-describedby={field.helpText ? `${field.name}-help` : undefined}
          />
        );

      case 'select':
        return (
          <select
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
            defaultValue={field.defaultValue}
            className={baseInputClass}
            aria-label={field.label}
            aria-required={field.required}
            aria-invalid={!!error}
          >
            <option value="">Select an option</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option.value}
                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  isDark
                    ? 'border-gray-700 hover:bg-gray-800'
                    : 'border-gray-200 hover:bg-gray-50'
                } ${value === option.value ? 'ring-2 ring-blue-500' : ''}`}
              >
                <input
                  type="radio"
                  {...register(field.name, {
                    required: field.required ? `${field.label} is required` : false,
                  })}
                  value={option.value}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  aria-label={option.label}
                />
                <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        if (field.options && field.options.length > 1) {
          // Multiple checkboxes
          return (
            <div className="space-y-2">
              {field.options.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    isDark
                      ? 'border-gray-700 hover:bg-gray-800'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    {...register(field.name)}
                    value={option.value}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    aria-label={option.label}
                  />
                  <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          );
        } else {
          // Single checkbox
          return (
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                {...register(field.name, {
                  required: field.required ? `${field.label} is required` : false,
                })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                aria-label={field.label}
              />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {field.placeholder || field.label}
              </span>
            </label>
          );
        }

      case 'file':
        return (
          <div className="space-y-2">
            <input
              type="file"
              {...register(field.name, {
                required: field.required ? `${field.label} is required` : false,
                validate: (files: FileList) => {
                  if (!files || files.length === 0) return true;
                  
                  const file = files[0];
                  
                  // File size validation
                  if (field.validation?.fileMaxSize) {
                    const sizeMB = file.size / (1024 * 1024);
                    if (sizeMB > field.validation.fileMaxSize) {
                      return `File size must be less than ${field.validation.fileMaxSize}MB`;
                    }
                  }
                  
                  // File type validation
                  if (field.validation?.fileTypes && field.validation.fileTypes.length > 0) {
                    if (!field.validation.fileTypes.includes(file.type)) {
                      const types = field.validation.fileTypes.map(t => t.split('/')[1]).join(', ');
                      return `Allowed file types: ${types}`;
                    }
                  }
                  
                  return true;
                },
              })}
              accept={field.validation?.fileTypes?.join(',')}
              className={baseInputClass}
              aria-label={field.label}
              aria-required={field.required}
              aria-invalid={!!error}
            />
            {field.validation?.fileMaxSize && (
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Max file size: {field.validation.fileMaxSize}MB
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
            className={baseInputClass}
            aria-label={field.label}
            aria-required={field.required}
            aria-invalid={!!error}
          />
        );

      case 'signature':
        return (
          <>
            <input
              type="hidden"
              {...register(field.name, {
                required: field.required ? `${field.label} is required` : false,
              })}
            />
            <SignatureField
              value={value}
              onChange={(val) => setValue(field.name, val, { shouldValidate: true })}
              label={field.label}
              required={field.required}
              error={error?.message as string}
              theme={theme}
            />
          </>
        );

      case 'calculated':
        return (
          <input
            type="text"
            value={value || '0'}
            readOnly
            className={`${baseInputClass} bg-gray-100 cursor-not-allowed`}
            aria-label={field.label}
            aria-readonly="true"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
              min: field.validation?.min ? {
                value: field.validation.min,
                message: `Minimum value is ${field.validation.min}`
              } : undefined,
              max: field.validation?.max ? {
                value: field.validation.max,
                message: `Maximum value is ${field.validation.max}`
              } : undefined,
            })}
            placeholder={field.placeholder}
            defaultValue={field.defaultValue}
            min={field.validation?.min}
            max={field.validation?.max}
            step="any"
            className={baseInputClass}
            aria-label={field.label}
            aria-required={field.required}
            aria-invalid={!!error}
          />
        );

      case 'option':
        return (
          <div className="flex flex-wrap gap-2" role="group" aria-label={field.label}>
            {field.options?.map((option) => {
              const isSelected = value === option.value;
              return (
                <label
                  key={option.value}
                  className={`cursor-pointer px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all select-none ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : isDark
                      ? 'border-gray-600 bg-gray-800 text-gray-200 hover:border-gray-400'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    {...register(field.name, {
                      required: field.required ? `${field.label} is required` : false,
                    })}
                    value={option.value}
                    className="sr-only"
                    aria-label={option.label}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        );

      default:
        return (
          <input
            type={field.type}
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
              minLength: field.validation?.minLength ? {
                value: field.validation.minLength,
                message: `Minimum ${field.validation.minLength} characters required`
              } : undefined,
              maxLength: field.validation?.maxLength ? {
                value: field.validation.maxLength,
                message: `Maximum ${field.validation.maxLength} characters allowed`
              } : undefined,
              pattern: field.validation?.pattern ? {
                value: new RegExp(field.validation.pattern),
                message: field.validation.patternMessage || 'Invalid format'
              } : undefined,
            })}
            placeholder={field.placeholder}
            defaultValue={field.defaultValue}
            className={baseInputClass}
            aria-label={field.label}
            aria-required={field.required}
            aria-invalid={!!error}
          />
        );
    }
  };

  return (
    <div className={`space-y-1 ${widthClass}`}>
      {field.type !== 'checkbox' && field.type !== 'signature' ? (
        <label htmlFor={field.name} className={labelClass}>
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
          {field.type === 'calculated' && (
            <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              (Calculated)
            </span>
          )}
        </label>
      ) : null}

      {renderField()}

      {field.helpText && (
        <p 
          id={`${field.name}-help`}
          className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
        >
          {field.helpText}
        </p>
      )}

      {error && field.type !== 'signature' && (
        <div className="flex items-center gap-2 text-red-500 text-sm mt-1" role="alert">
          <AlertCircle className="w-4 h-4" />
          <span>{error.message as string}</span>
        </div>
      )}
    </div>
  );
};
